/* One-shot WebGPU border light. No dependencies, telemetry, or checkout hooks. */
(() => {
  const card = document.querySelector('[data-yp-saqr="true"] .yp-saqr-featured');
  if (!card || card.dataset.lightState) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  card.dataset.lightState = 'waiting';
  if (motion.matches || !navigator.gpu) {
    card.dataset.lightState = motion.matches ? 'reduced-motion' : 'unsupported';
    return;
  }
  let device, context, canvas, frame, timeout, observer, resizeObserver;
  let stopped = false;
  function stop(state) {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(frame);
    clearTimeout(timeout);
    observer?.disconnect();
    resizeObserver?.disconnect();
    motion.removeEventListener('change', onMotion);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    context?.unconfigure();
    canvas?.remove();
    device?.destroy();
    card.dataset.lightState = state;
  }
  function onMotion() { if (motion.matches) stop('reduced-motion'); }
  function onVisibility() { if (document.hidden) stop('hidden'); }
  function onPageHide() { stop('pagehide'); }
  async function start() {
    observer.disconnect();
    if (motion.matches || document.hidden) return stop(motion.matches ? 'reduced-motion' : 'hidden');
    card.dataset.lightState = 'initializing';
    timeout = setTimeout(() => stop('timeout'), 6000);
    motion.addEventListener('change', onMotion);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    try {
      const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'low-power' });
      if (stopped) return;
      if (!adapter) return stop('no-adapter');
      const acquired = await adapter.requestDevice();
      if (stopped) { acquired.destroy(); return; }
      device = acquired;
      device.lost.then(() => { if (!stopped) stop('device-lost'); });
      device.addEventListener('uncapturederror', () => stop('gpu-error'));
      canvas = document.createElement('canvas');
      canvas.className = 'yp-featured-light';
      canvas.setAttribute('aria-hidden', 'true');
      card.append(canvas);
      context = canvas.getContext('webgpu');
      if (!context) return stop('no-context');
      const format = navigator.gpu.getPreferredCanvasFormat();
      context.configure({device, format, alphaMode: 'premultiplied'});
      const buffer = device.createBuffer({size: 64, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST});
      const module = device.createShaderModule({code: `
struct U { size: vec2f, time: f32, radius: f32, violet: vec4f, pink: vec4f, gold: vec4f }
@group(0) @binding(0) var<uniform> u: U;
@vertex fn vs(@builtin(vertex_index) i: u32) -> @builtin(position) vec4f {
  var points = array<vec2f, 3>(vec2f(-1,-1), vec2f(3,-1), vec2f(-1,3));
  return vec4f(points[i], 0, 1);
}
@fragment fn fs(@builtin(position) pos: vec4f) -> @location(0) vec4f {
  let p = pos.xy - u.size * .5;
  let halfSize = u.size * .5 - vec2f(1.7);
  let q = abs(p) - halfSize + vec2f(u.radius);
  let distance = length(max(q, vec2f(0))) + min(max(q.x,q.y),0) - u.radius;
  let angle = atan2(p.y / halfSize.y, p.x / halfSize.x);
  let phase = angle - (u.time * 6.283185 - 1.570796);
  let head = pow(max(0., cos(phase)), 18.);
  let trail = pow(max(0., cos(phase + .55)), 5.) * .45;
  let sweep = head + trail;
  let core = exp(-pow(distance / 1.25, 2.));
  let halo = exp(-abs(distance) / 5.5) * .42;
  let fade = smoothstep(0., .13, u.time) * (1. - smoothstep(.72, 1., u.time));
  let tint = mix(u.violet.rgb, u.pink.rgb, .5 + .5 * sin(phase));
  let color = mix(tint, u.gold.rgb, head * .8);
  let alpha = clamp((core + halo) * sweep * fade, 0., 1.);
  return vec4f(color * alpha, alpha);
}`});
      const info = await module.getCompilationInfo();
      if (stopped) return;
      if (info.messages.some(m => m.type === 'error')) return stop('compile-error');
      const pipeline = await device.createRenderPipelineAsync({layout:'auto',vertex:{module,entryPoint:'vs'},fragment:{module,entryPoint:'fs',targets:[{format}]},primitive:{topology:'triangle-list'}});
      if (stopped) return;
      const group = device.createBindGroup({layout:pipeline.getBindGroupLayout(0),entries:[{binding:0,resource:{buffer}}]});
      // Sample the existing CTA gradient so the light follows the design system.
      const button = card.querySelector('[data-yp-button][data-type="plus"], button');
      const colors = getComputedStyle(button).backgroundImage.match(/rgba?\([^)]+\)/g);
      if (!colors || colors.length < 3) return stop('missing-brand-colors');
      const rgb = color => color.match(/[\d.]+/g).slice(0,3).map(Number).map(v => v / 255);
      const values = new Float32Array(16);
      values.set([...rgb(colors[0]),1],4);
      values.set([...rgb(colors[Math.floor(colors.length * .7)]),1],8);
      values.set([...rgb(colors[colors.length-1]),1],12);
      function resize() {
        const rect = canvas.getBoundingClientRect();
        const scale = Math.min(devicePixelRatio || 1, 1.5, 1600 / Math.max(rect.width,rect.height));
        canvas.width = Math.max(1,Math.round(rect.width * scale));
        canvas.height = Math.max(1,Math.round(rect.height * scale));
        values[0] = canvas.width; values[1] = canvas.height;
        values[3] = parseFloat(getComputedStyle(card).borderTopLeftRadius) * scale;
      }
      resize();
      resizeObserver = new ResizeObserver(resize); resizeObserver.observe(card);
      clearTimeout(timeout);
      card.dataset.lightState = 'playing';
      const started = performance.now();
      function draw(now) {
        if (stopped) return;
        const progress = (now - started) / 2800;
        if (progress >= 1) return stop('complete');
        values[2] = progress;
        try {
          device.queue.writeBuffer(buffer, 0, values);
          const encoder = device.createCommandEncoder();
          const pass = encoder.beginRenderPass({colorAttachments:[{view:context.getCurrentTexture().createView(),clearValue:{r:0,g:0,b:0,a:0},loadOp:'clear',storeOp:'store'}]});
          pass.setPipeline(pipeline); pass.setBindGroup(0,group); pass.draw(3); pass.end();
          device.queue.submit([encoder.finish()]);
          frame = requestAnimationFrame(draw);
        } catch { stop('render-error'); }
      }
      frame = requestAnimationFrame(draw);
    } catch { stop('unavailable'); }
  }
  observer = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting)) start();
  }, {threshold:.12});
  observer.observe(card);
})();
